import React, { useEffect, useState } from "react";

import useLocalization from "../../hooks/useLocalization";
import useData from "../../hooks/useData";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Input from "../elements/Input";
import InfoBox from "../layout/InfoBox";
import CustomField from "../ui/CustomField";
import EditorToolbar from "./EditorToolbar";
import ImageUpload from "../elements/ImageUpload";
import Currency from "../elements/Currency";
import Switch from "../elements/Switch";
import SkuPricingView from "../ui/SkuPricingView";
import SkuListingView from "../ui/SkuListingView";

const SkuEditor = ({ sku, onSave, clientId }) => {
  const { getLabel, getMessage } = useLocalization();
  const { updateRecord, deleteRecord } = useData();

  const [updatedSku, setUpdatedSku] = useState(sku ? sku : {});
  const [skuImage, setSkuImage] = useState(sku && sku.image ? sku.image : null);
  const [openImageUpload, setOpenImageUpload] = useState(false);

  // useEffect(() => {
  //   console.log("clientId: ", clientId);
  // }, [clientId]);

  const handleUpdate = (field, value) => {
    setUpdatedSku((current) => {
      return { ...current, [field]: value };
    });
  };

  const handleCustomFieldChange = (originalName, field) => {
    setUpdatedSku((current) => {
      let fields = [];

      if (Array.isArray(current.fields)) {
        fields = [...current.fields];
      }

      const fieldIndex = fields.findIndex((f) => f.name === originalName);

      if (fieldIndex === -1) {
        fields.push(field);
      } else {
        fields[fieldIndex] = field;
      }

      return { ...current, fields };
    });
  };

  const handleImageUploaded = (base64) => {
    setSkuImage(base64);
    handleUpdate("image", base64);
  };

  const handleRemoveImage = () => {
    setSkuImage(null);
    handleUpdate("image", null);
  };

  const handleSave = async () => {
    const results = await updateRecord("SKU", updatedSku, true);

    if (results.isSuccessful) {
      onSave();
    }
  };

  const handleDelete = async () => {
    const results = await deleteRecord("SKU", updatedSku);

    if (results.isSuccessful) {
      onSave();
    }
  };

  return (
    <>
      <Box>
        <EditorToolbar
          onSave={handleSave}
          onDelete={sku && sku.id ? handleDelete : null}
          message={sku.id}
        />
        <Box sx={{ padding: "10px" }}>
          <Box>
            <Box
              sx={{ display: "flex" }}
              gap={1}
            >
              <Box flex={1}>
                <ImageUpload
                  label="Upload Image"
                  value={skuImage}
                  onChange={handleImageUploaded}
                  containerStyle={{ width: "200px" }}
                  displayWidth="200px"
                />
                <Box
                  sx={{
                    width: "200px",
                    display: "flex",
                    justifyContent: "center",
                    mt: 1,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleRemoveImage}
                    disabled={!skuImage && !updatedSku?.image}
                  >
                    Delete Image
                  </Button>
                </Box>
              </Box>
              <Box flex={5}>
                <Box
                  sx={{ display: "flex" }}
                  flex={1}
                  gap={1}
                >
                  <Box sx={{ flex: 1 }}>
                    <Input
                      label={getLabel("name")}
                      value={updatedSku?.name}
                      onChange={(newValue) => handleUpdate("name", newValue)}
                      fullwidth
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Input
                      label={getLabel("name_fr")}
                      value={updatedSku?.name_fr}
                      fullwidth
                      onChange={(newValue) => handleUpdate("name_fr", newValue)}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{ display: "flex" }}
                  flex={1}
                  gap={1}
                >
                  <Input
                    label={getLabel("upc")}
                    value={updatedSku?.upc}
                    onChange={(newValue) => handleUpdate("upc", newValue)}
                  />
                  <Input
                    label={getLabel("packSize")}
                    value={updatedSku?.packSize}
                    onChange={(newValue) => handleUpdate("packSize", newValue)}
                  />
                  <Currency
                    label={getLabel("price")}
                    value={updatedSku?.price}
                    onChange={(newValue) => handleUpdate("price", newValue)}
                  />
                </Box>
                <Box
                  sx={{ display: "flex" }}
                  flex={1}
                  gap={1}
                >
                  <Input
                    label={getLabel("baseUom")}
                    value={updatedSku?.baseUom}
                    onChange={(newValue) => handleUpdate("baseUom", newValue)}
                  />
                  <Input
                    label={getLabel("unitsPerPack")}
                    value={updatedSku?.unitsPerPack}
                    onChange={(newValue) =>
                      handleUpdate("unitsPerPack", newValue)
                    }
                    type="number"
                  />
                  <Input
                    label={getLabel("unitsPerCase")}
                    value={updatedSku?.unitsPerCase}
                    onChange={(newValue) =>
                      handleUpdate("unitsPerCase", newValue)
                    }
                    type="number"
                  />
                </Box>
                <Box
                  sx={{ display: "flex" }}
                  gap={1}
                >
                  <Input
                    label={getLabel("category")}
                    value={updatedSku?.category}
                    onChange={(newValue) => handleUpdate("category", newValue)}
                  />
                  <Input
                    label={getLabel("family")}
                    value={updatedSku?.family}
                    onChange={(newValue) => handleUpdate("family", newValue)}
                  />
                  <Input
                    label={getLabel("clientCode")}
                    value={updatedSku?.clientCode}
                    onChange={(newValue) =>
                      handleUpdate("clientCode", newValue)
                    }
                  />
                  <Switch
                    label="Active"
                    value={updatedSku?.isActive !== false}
                    onChange={(newValue) => handleUpdate("isActive", newValue)}
                  />
                  <Switch
                    label="Inventory Item"
                    value={updatedSku?.isInventoryItem === true}
                    onChange={(newValue) =>
                      handleUpdate("isInventoryItem", newValue)
                    }
                  />
                </Box>
                <Box
                  sx={{ display: "flex" }}
                  gap={1}
                >
                  <Input
                    label={getLabel("roiDistribution")}
                    value={updatedSku?.roiDistribution}
                    onChange={(newValue) =>
                      handleUpdate("roiDistribution", newValue)
                    }
                    type="number"
                  />
                  <Input
                    label={getLabel("roiOOS")}
                    value={updatedSku?.roiOOS}
                    onChange={(newValue) => handleUpdate("roiOOS", newValue)}
                    type="number"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ padding: "10px" }}>
          <InfoBox
            label={getLabel("customFields")}
            sx={{ backgroundColor: "#eee" }}
          >
            {Array.isArray(sku.fields) && sku.fields.length > 0 ? (
              <Stack spacing={1}>
                {updatedSku.fields.map((field) => (
                  <CustomField
                    field={field}
                    onChange={handleCustomFieldChange}
                  />
                ))}
              </Stack>
            ) : (
              <Typography>{getMessage("noRecords")}</Typography>
            )}
          </InfoBox>
        </Box>

        {clientId && (
          <>
            <Box sx={{ padding: "10px" }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Pricing</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <SkuPricingView
                    sku={sku}
                    clientId={clientId}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>

            <Box sx={{ padding: "10px" }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Listings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <SkuListingView
                    sku={sku}
                    clientId={clientId}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default SkuEditor;
